import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import postal from 'postal';
import ErrorBoundary from '../../../shared/error-boundary';
import Loading from '../../../shared/loading';
import defaultOnErrorHandler from '../../../shared/default-on-error-handler';
import {
  SortableFilterableTableHeader,
  SORT_DIRECTION,
  filterSimpleColumn,
  sortSimpleColumn,
} from '../../../shared/sortable-filterable-table-header';
import EditPartnershipModal from '../partnerships/edit-partnership-modal';
import { getById } from '../../../services/lwadmin/partnership/partnership-service';

function filterWires(wires, state) {
  let filteredWires = _(wires);

  filteredWires = filterSimpleColumn(filteredWires, 'name', state);
  filteredWires = filterSimpleColumn(filteredWires, 'title', state);

  return filteredWires.value();
}

function sortWires(wires, state) {
  let sortedWires = _(wires);

  sortedWires = sortSimpleColumn(sortedWires, 'name', state);
  sortedWires = sortSimpleColumn(sortedWires, 'title', state);

  return sortedWires.value();
}

function filterAndSortWires(wires, state) {
  return sortWires(filterWires(wires, state), state);
}

const Wire = (source, contributor) => ({
  ...(source || {
    id: null,
    title: '',
    sent_contact_count: 0,
    unsent_contact_count: 0,
  }),
  contributor,
  name: contributor.name,
});

function Contributor(source) {
  _.assign(this, source || {
    id: null,
    active: false,
    name: '',
    company: '',
  });
  this.wires = _.get(source, 'wires', []).map(x => Wire(x, this))
    .filter(x => x.sent_contact_count > 0 || x.unsent_contact_count > 0);
}

function Partnership(source) {
  _.assign(this, source || {
    id: null,
    name: '',
    active: false,
    send_automatically: false,
    contact_email: '',
    partnership_type: '',
    contributors: [],
  });
  this.contributors = this.contributors.map(x => new Contributor(x));
  this.wires = _.flatten(this.contributors.map(c => c.wires));
}

export default class WiresAdmin extends React.Component {
  static propTypes = {
    partnershipId: PropTypes.number.isRequired,
  }

  constructor(props, context) {
    super(props, context);

    this.state = {
      partnership: new Partnership(),
      wires: [],
      nameSortDirection: SORT_DIRECTION.unsorted,
      nameFilter: '',
      titleSortDirection: SORT_DIRECTION.unsorted,
      titleFilter: '',
    };
  }

  componentDidMount() {
    postal.subscribe({
      channel: 'lwadmin.partnership.commands',
      topic: 'edit-partnership',
      callback: () => this.editPartnership(),
    });
    this.refresh();
  }

  refresh() {
    this.loading.start();
    getById(this.props.partnershipId)
      .then(result => this.setState(state => {
        state.partnership = new Partnership(result.data);
        state.wires = filterAndSortWires(state.partnership.wires, state);
        return state;
      }))
      .catch(error => defaultOnErrorHandler(error, 'Error loading partnership'))
      .finally(() => this.loading.stop());
  }

  editPartnership() {
    this.editPartnershipModal.present(this.state.partnership.id);
  }

  sortContributorName(direction) {
    this.setState(state => {
      state.nameSortDirection = direction;
      state.wires = filterAndSortWires(state.partnership.wires, state);
      return state;
    });
  }

  filterContributorName(filter) {
    this.setState(state => {
      state.nameFilter = filter;
      state.wires = filterAndSortWires(state.partnership.wires, state);
      return state;
    });
  }

  sortWireTitle(direction) {
    this.setState(state => {
      state.titleSortDirection = direction;
      state.wires = filterAndSortWires(state.partnership.wires, state);
      return state;
    });
  }

  filterWireTitle(filter) {
    this.setState(state => {
      state.titleFilter = filter;
      state.wires = filterAndSortWires(state.partnership.wires, state);
      return state;
    });
  }

  renderWiresTable() {
    return (
      <table className="table table-hover">
        <thead>
          <tr>
            <SortableFilterableTableHeader
              onSort={direction => this.sortContributorName(direction)}
              onFilter={filter => this.filterContributorName(filter)}
            >
              Contributor name
            </SortableFilterableTableHeader>
            <SortableFilterableTableHeader
              onSort={direction => this.sortWireTitle(direction)}
              onFilter={filter => this.filterWireTitle(filter)}
            >
              Wire title
            </SortableFilterableTableHeader>
            <th>Sent enquiries</th>
            <th>
              <a href={`/lwadmin/partnership/${this.props.partnershipId}/all`}>See all enquiries</a>
            </th>
          </tr>
        </thead>
        <tbody>
          {this.state.wires.map(wire => (
            <tr key={wire.id}>
              <td>{wire.contributor.name}</td>
              <td>{wire.title}</td>
              <td>{`${wire.sent_contact_count}/${wire.sent_contact_count + wire.unsent_contact_count}`}</td>
              <td className="table-tools">
                <ul>
                  <li>
                    <a href={`/lwadmin/partnership/${this.props.partnershipId}/wire/${wire.id}`}>View Enquiries</a>
                  </li>
                </ul>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  render() {
    return (
      <ErrorBoundary>
        <div className="lwadmin-partnership-admin">
          <Loading ref={c => (this.loading = c)}>
            <h1>
              <a href="/lwadmin/partnerships">Partnerships</a>
              {` | ${this.state.partnership.name}`}
            </h1>
            {!this.state.partnership.active && <p className="alert alert-warning">This partnership has been deactivated</p>}
            <p>
              <strong>Partnership type:</strong>
              {` ${this.state.partnership.partnership_type}`}
              <br />
              <strong>
                {
                  this.state.partnership.send_automatically
                    ? <span>Contacts are sent automatically</span>
                    : (
                      <span>
                        {'Contacts are '}
                        <em>not</em>
                        {' sent automatically'}
                      </span>
                    )
                }
              </strong>
              <br />
              <strong>Contact:</strong>
              {' '}
              <a href={`mailto:${this.state.partnership.contact_email}`}>{this.state.partnership.contact_email}</a>
            </p>

            <section data-role="tab-set">
              <nav>
                <ul>
                  <li><a href="#wires" className="active">Wires</a></li>
                </ul>
              </nav>
              <section data-role="tab-panel" id="wires" className="active">
                {this.renderWiresTable()}
              </section>
            </section>
          </Loading>
          <EditPartnershipModal
            ref={c => (this.editPartnershipModal = c)}
            partnershipDidSave={() => this.refresh()}
          />
        </div>
      </ErrorBoundary>
    );
  }
}
